/**
 * Lazy-loader for the visual editor (~891KB visual-editor.js).
 *
 * That bundle was loaded on EVERY admin page even though most page views never
 * edit. This installs a lightweight stub `window.VisualEditor` so other scripts
 * (e.g. sidebar-detail) can reference it safely, and fetches the real
 * bundle on first edit intent (open/openArtist/addComponent). The real bundle
 * does `var VisualEditor = (function(){…})()`, which reassigns window.VisualEditor,
 * replacing this stub. It has no DOMContentLoaded init, so loading it late is safe.
 */
(function () {
  // If the real editor is somehow already present, don't stub over it.
  if (window.VisualEditor && !window.VisualEditor._stub) return;

  var v = window.ASSET_VERSION ? '?v=' + window.ASSET_VERSION : '';
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src + v;
      s.onload = resolve;
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }
  function loadCss(href) {
    return new Promise(function (resolve) {
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href + v;
      l.onload = resolve;
      l.onerror = resolve; // missing CSS shouldn't block the editor outright
      document.head.appendChild(l);
    });
  }

  // The editor's stylesheet, once. Its modals (the Share / QR modal) open without the editor on phones, so they load just this.
  var cssPromise = null;
  function loadEditorCss() { return cssPromise || (cssPromise = loadCss('/css/visual-editor.css')); }
  window.loadEditorCss = loadEditorCss;

  var loadingPromise = null;
  function loadRealEditor() {
    if (loadingPromise) return loadingPromise;
    // CRITICAL PATH = only the editor bundle + its CSS. The companions (synonyms, playlist seed/
    // generator, sticker cutout, mix/soundbed engines) are referenced ONLY at INTERACTION time via
    // window.* lookups — nothing on the open path needs them — so kick them off in the BACKGROUND and
    // do NOT gate open() on them. They finish well before a user reaches those features, and open()
    // no longer waits on the ~130KB of companions (mix-engine alone is 84KB). James 2026-09-26.
    ['/js/synonym-dictionary.js', '/js/playlist-seed.js', '/js/playlist-generator.js',
     '/js/sticker-cutout.js', '/js/mix-engine.js', '/js/soundbed-mix.js']
      .forEach(function (s) { loadScript(s).catch(function () {}); });
    loadingPromise = Promise.all([
      loadScript('/js/visual-editor.js'),
      loadEditorCss()
    ]).catch(function (e) { loadingPromise = null; throw e; });
    return loadingPromise;
  }
  // Exposed so callers can warm the bundle on intent (e.g. hover/expand) before click.
  window.loadVisualEditor = loadRealEditor;

  // Boot overlay: the bundle fetch + editor boot used to hold a bare white screen (the demo
  // problem). A scheme-matched scrim with the app's two-dot loader covers the gap from edit
  // intent until the editor has taken over (the gooey preview then shows its own dots while
  // the canvas re-renders). Failsafe-removed so a stall can never trap the admin behind it.
  var bootOverlayTimer = null;
  // SUPPRESSED: the editor-open gate (sidebar-detail openInEditMode) shows its loader INSIDE the editor
  // frame and owns the wait, so the full-screen boot screen must not appear on top of it (James
  // 2026-09-30: one loader, in the frame).
  var bootOverlaySuppressed = false;
  // "Opening editor for <Release>" / "Opening profile for <Artist>" — named after what's
  // opening (James: these little things make polish happen). Names come from SidebarDetail's
  // caches; absent → the plain verb line.
  function bootLabel(method) {
    try {
      var sd = window.SidebarDetail;
      if (method === 'openArtist') {
        var a = sd && sd.getCachedArtist && sd.getCachedArtist();
        var an = a && (a.name || a.artist_name);
        return an ? 'Opening profile for ' + an : 'Opening profile';
      }
      var p = sd && sd.getCachedProduct && sd.getCachedProduct();
      var pn = p && (p.product_title || p.name);
      return pn ? 'Opening editor for ' + pn : 'Opening editor';
    } catch (_) { return 'Opening editor'; }
  }
  function escapeText(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }
  function showBootOverlay(method, labelOverride) {
    if (bootOverlaySuppressed) return;
    if (document.getElementById('ve-boot-loader')) return;
    var el = document.createElement('div');
    el.id = 've-boot-loader';
    el.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;flex-direction:column;gap:18px;' +
      'align-items:center;justify-content:center;background:var(--bg-color,#0a0a0a);opacity:0;transition:opacity 0.15s ease;';
    // .generating-spinner = the universal two-dot loader retrofit (style.css); colour follows scheme.
    el.innerHTML = '<div class="generating-spinner" style="color:var(--text-color,#fff);"></div>' +
      '<div style="font:500 12px/1.4 -apple-system,system-ui,sans-serif;letter-spacing:0.04em;' +
        'color:var(--text-color,#fff);opacity:0.55;">' + escapeText(labelOverride || bootLabel(method)) +
        // Animated ellipsis: three dots fade in one after another, on a loop — it's working. They
        // always hold their space, so the line never shifts.
        '<span class="ve-boot-dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span></div>';
    if (!document.getElementById('ve-boot-dots-style')) {
      var st = document.createElement('style');
      st.id = 've-boot-dots-style';
      st.textContent = '.ve-boot-dots span{opacity:0;animation:veBootDot 1.2s infinite}' +
        '.ve-boot-dots span:nth-child(2){animation-delay:.2s}.ve-boot-dots span:nth-child(3){animation-delay:.4s}' +
        '@keyframes veBootDot{0%,20%{opacity:0}40%,80%{opacity:1}100%{opacity:0}}' +
        '@media (prefers-reduced-motion:reduce){.ve-boot-dots span{animation:none;opacity:1}}';
      document.head.appendChild(st);
    }
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.style.opacity = '1'; });
    clearTimeout(bootOverlayTimer);
    bootOverlayTimer = setTimeout(hideBootOverlay, 15000);   // never trap the UI behind a stall
  }
  function hideBootOverlay() {
    clearTimeout(bootOverlayTimer);
    var el = document.getElementById('ve-boot-loader');
    if (!el) return;
    el.style.opacity = '0';
    setTimeout(function () { if (el.parentNode) el.remove(); }, 200);
  }

  window.EditorBootOverlay = {
    suppress: function (on) { bootOverlaySuppressed = !!on; if (on) hideBootOverlay(); },
  };

  // On-screen error reporter — surfaces the ACTUAL reason the editor failed to open, so a device
  // without a console (iPad) can read it. Always shown on a genuine open failure (better than the
  // old silent shell-with-no-tray); a debug flag (?vedebug=1 or localStorage veDebug=1) also routes
  // uncaught errors + promise rejections here, catching failures that never reach the open() path.
  function showEditorError(title, detail) {
    try {
      var safe = function (s) { var d = document.createElement('div'); d.textContent = String(s == null ? '' : s); return d.innerHTML; };
      var el = document.getElementById('ve-error-report');
      if (!el) {
        el = document.createElement('div');
        el.id = 've-error-report';
        el.style.cssText = 'position:fixed;left:12px;right:12px;bottom:12px;z-index:100001;background:#2a0d0d;' +
          'color:#ffd7d7;border:1px solid #ff5a5a;border-radius:12px;padding:14px 16px;max-height:45vh;overflow:auto;' +
          '-webkit-overflow-scrolling:touch;box-shadow:0 12px 48px rgba(0,0,0,0.5);' +
          'font:500 12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;';
        document.body.appendChild(el);
      }
      el.innerHTML =
        '<div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:8px;">' +
          '<strong style="color:#ff8a8a;font-family:-apple-system,system-ui,sans-serif;">Editor error</strong>' +
          '<button type="button" id="ve-error-x" style="background:rgba(255,255,255,0.12);border:none;color:#fff;border-radius:8px;padding:6px 12px;font:600 13px -apple-system,system-ui,sans-serif;">Dismiss</button>' +
        '</div>' +
        '<div style="font-family:-apple-system,system-ui,sans-serif;color:#ffbdbd;margin-bottom:6px;">' + safe(title) + '</div>' +
        '<pre style="white-space:pre-wrap;word-break:break-word;margin:0;color:#ffd7d7;">' + safe(detail) + '</pre>';
      var x = el.querySelector('#ve-error-x');
      if (x) x.addEventListener('click', function () { el.remove(); });
    } catch (_) { /* reporter must never throw */ }
  }

  // Debug capture (opt-in): route every uncaught error / rejection to the on-screen reporter, so a
  // failure BEFORE the editor bundle even loads (e.g. a tap handler that never calls open()) is still
  // visible on the iPad. Enable with ?vedebug=1 in the URL or localStorage.setItem('veDebug','1').
  try {
    var _veDbg = /[?&]vedebug=1/i.test(location.search) ||
      (window.localStorage && localStorage.getItem('veDebug') === '1');
    if (_veDbg) {
      window.addEventListener('error', function (ev) {
        showEditorError('Uncaught error', (ev.error && (ev.error.stack || ev.error.message)) ||
          (ev.message + ' @ ' + (ev.filename || '?') + ':' + (ev.lineno || '?')));
      });
      window.addEventListener('unhandledrejection', function (ev) {
        var r = ev.reason;
        showEditorError('Unhandled promise rejection', (r && (r.stack || r.message)) || r);
      });
    }
  } catch (_) {}

  // Action methods: load the real bundle, then delegate to it. A failed bundle fetch (deploy in
  // progress, flaky network right after login) is retried once; if it still fails we ANNOUNCE it —
  // the old silent catch left the editor shell applied with no tray/panels and no way to recover
  // short of a refresh. sidebar-detail listens for the event and rolls the shell back.
  function lazy(method) {
    return function () {
      var args = arguments;
      var showLoader = method === 'open' || method === 'openArtist';
      if (showLoader) showBootOverlay(method);
      return loadRealEditor()
        .catch(function () { return loadRealEditor(); }) // loadingPromise resets on error → true retry
        .then(function () {
          return window.VisualEditor[method].apply(window.VisualEditor, args);
        })
        .then(function (r) {
          if (showLoader) hideBootOverlay();
          return r;
        })
        .catch(function (e) {
          if (showLoader) hideBootOverlay();
          console.error('[editor-loader]', e);
          showEditorError('The editor failed to open (' + method + ').', (e && (e.stack || e.message)) || e);
          try { window.dispatchEvent(new Event('visual-editor-load-failed')); } catch (_) {}
        });
    };
  }

  window.VisualEditor = {
    _stub: true,
    open: lazy('open'),
    openArtist: lazy('openArtist'),
    addComponent: lazy('addComponent'),
    // Query methods: until the real editor loads it is, by definition, not active.
    isActive: function () { return false; },
    getCurrentEntityId: function () { return null; },
    getPendingChanges: function () { return null; },
    getPropertiesHTML: function () { return ''; },
    wireFieldHandlers: function () {},
    close: function () {}
  };
})();
